import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiCpu, FiCheckCircle, FiAlertCircle, FiFileText, FiKey, FiLayout } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jobApi, resumeApi } from '../../services/api';
import { DisabledCreditButton } from '../../components/membership';
import PageHeader from '../../components/layout/PageHeader';
import { useMembership } from '../../contexts/MembershipContext';
import ScoreRing from '../../components/common/ScoreRing';
import ScoreBar from '../../components/common/ScoreBar';

const ATSScoring = () => {
  const { jobId } = useParams();
  const { refreshCreditBalance } = useMembership();
  
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [job, setJob] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [resumeVersions, setResumeVersions] = useState([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [scoreResult, setScoreResult] = useState(null);
  const [activeTab, setActiveTab] = useState('select'); // select, input, results
  
  useEffect(() => {
    fetchData();
  }, [jobId]);
  
  useEffect(() => {
    if (selectedResume) {
      loadResumeVersions(selectedResume);
    }
  }, [selectedResume]);
  
  useEffect(() => {
    if (selectedResume && selectedVersion && resumeVersions.length > 0) {
      // Get resume content
      const version = resumeVersions.find(v => v.versionId === selectedVersion);
      if (version && version.content) {
        // Convert content to plain text
        const text = extractTextFromContent(version.content);
        setResumeText(text);
      }
    }
  }, [selectedResume, selectedVersion, resumeVersions]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch job if jobId is provided
      if (jobId) {
        const jobResponse = await jobApi.getJobById(jobId);
        setJob(jobResponse.data);
        setJobDescription(jobResponse.data.description || '');
      }
      
      // Fetch resumes
      const resumesResponse = await resumeApi.getAllResumes();
      setResumes(resumesResponse.data);
      
      if (resumesResponse.data.length > 0) {
        setSelectedResume(resumesResponse.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadResumeVersions = async (resumeId) => {
    try {
      const response = await resumeApi.getResumeById(resumeId);
      const { versions } = response.data;
      setResumeVersions(versions);
      
      if (versions.length > 0) {
        setSelectedVersion(versions[versions.length - 1].versionId);
      }
    } catch (error) {
      console.error('Error loading resume versions:', error);
      toast.error('Failed to load resume versions');
    }
  };
  
  const extractTextFromContent = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    
    // Try to extract text from content structure
    let text = '';
    if (content.header?.name) text += content.header.name + '\n';
    if (content.header?.email) text += content.header.email + '\n';
    if (content.header?.phone) text += content.header.phone + '\n';
    
    const sections = ['summary', 'experience', 'skills', 'education', 'projects', 'certifications'];
    sections.forEach(section => {
      if (content[section]) {
        if (Array.isArray(content[section])) {
          content[section].forEach(item => {
            if (item.title) text += item.title + '\n';
            if (item.description) text += item.description + '\n';
            if (item.details) text += item.details + '\n';
          });
        } else if (typeof content[section] === 'object') {
          if (content[section].text) text += content[section].text + '\n';
        }
      }
    });
    
    return text;
  };

  const handleResumeChange = useCallback((e) => {
    setSelectedResume(e.target.value);
    setScoreResult(null);
  }, []);

  const handleVersionChange = useCallback((e) => {
    setSelectedVersion(e.target.value);
    setScoreResult(null);
  }, []);

  const calculateScore = async () => {
    if (!resumeText || !jobDescription) {
      toast.error('Please provide both resume and job description');
      return;
    }
    
    try {
      setAnalyzing(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/ats/score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          resume_text: resumeText,
          job_description: jobDescription
        })
      });
      
      if (!response.ok) {
        if (response.status === 402) {
          toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
          return;
        }
        throw new Error('ATS scoring failed');
      }
      
      const result = await response.json();
      setScoreResult(result);
      setActiveTab('results');
      
      // Refresh credit balance
      await refreshCreditBalance();
      
      toast.success('ATS scoring completed');
    } catch (error) {
      console.error('Error calculating ATS score:', error);
      toast.error('Failed to calculate ATS score');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        title="ATS Score Check"
        description="Check how well your resume matches a job description against Applicant Tracking Systems"
        whatYouCanDo={[
          'See how ATS systems parse your resume',
          'Identify missing keywords',
          'Get improvement recommendations'
        ]}
        whatYouShouldDo={[
          'Tailor your resume for each job',
          'Use relevant keywords from the job description',
          'Keep formatting ATS-friendly'
        ]}
        actions={
          jobId ? (
            <Link
              to={`/jobs/${jobId}`}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <FiArrowLeft className="mr-2 h-4 w-4" />
              Back to Job Details
            </Link>
          ) : null
        }
      />

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" role="tablist" aria-label="ATS scoring steps">
          <button
            role="tab"
            aria-selected={activeTab === 'select'}
            onClick={() => setActiveTab('select')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'select'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiFileText className="inline-block mr-2" aria-hidden="true" />
            Select Resume & Job
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'input'}
            onClick={() => setActiveTab('input')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'input'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiFileText className="inline-block mr-2" aria-hidden="true" />
            Manual Input
          </button>
          {scoreResult && (
            <button
              role="tab"
              aria-selected={activeTab === 'results'}
              onClick={() => setActiveTab('results')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'results'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiCheckCircle className="inline-block mr-2" aria-hidden="true" />
              Results
            </button>
          )}
        </nav>
      </div>

      {/* Select Resume & Job Tab */}
      {activeTab === 'select' && (
        <div className="card overflow-hidden mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-gray-700">
                  Select Resume
                </label>
                <select
                  id="resume"
                  name="resume"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={selectedResume}
                  onChange={handleResumeChange}
                >
                  <option value="">Select a Resume</option>
                  {resumes.map(resume => (
                    <option key={resume.id} value={resume.id}>
                      {resume.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="version" className="block text-sm font-medium text-gray-700">
                  Select Version
                </label>
                <select
                  id="version"
                  name="version"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={selectedVersion}
                  onChange={handleVersionChange}
                  disabled={!selectedResume}
                >
                  <option value="">Select a Version</option>
                  {resumeVersions.map(version => (
                    <option key={version.versionId} value={version.versionId}>
                      {version.customName || version.versionId} ({version.versionType})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {job && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700">Job: {job.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{job.company}</p>
              </div>
            )}
            
            <div className="mt-6 flex justify-center">
              <button
                onClick={calculateScore}
                disabled={analyzing || !selectedResume || !selectedVersion || !jobDescription}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {analyzing ? (
                  <>
                    <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                    Calculate ATS Score
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Input Tab */}
      {activeTab === 'input' && (
        <div className="card overflow-hidden mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              <div>
                <label htmlFor="resume-text" className="block text-sm font-medium text-gray-700">
                  Resume Content (paste your resume text)
                </label>
                <textarea
                  id="resume-text"
                  rows={10}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Paste your resume content here..."
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="job-description" className="block text-sm font-medium text-gray-700">
                  Job Description
                </label>
                <textarea
                  id="job-description"
                  rows={8}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Paste the job description here..."
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                />
              </div>
              
              <div className="flex justify-center">
                <button
                  onClick={calculateScore}
                  disabled={analyzing || !resumeText || !jobDescription}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {analyzing ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                      Calculate ATS Score
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && scoreResult && (() => {
        const overallScore = scoreResult.overall_score || 0;
        const matchedCount = scoreResult.keyword_analysis?.matched?.length || 0;
        const missingCount = scoreResult.keyword_analysis?.missing?.length || 0;
        const keywordTotal = matchedCount + missingCount;
        const keywordPct = keywordTotal > 0 ? Math.round((matchedCount / keywordTotal) * 100) : 0;

        return (
        <div className="space-y-6">
          {/* Overall Score + breakdown sidebar */}
          <div className="card overflow-hidden">
            <div className="px-6 py-8 sm:p-10 flex flex-col md:flex-row items-center md:items-start gap-10">
              <div className="flex flex-col items-center flex-shrink-0">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-5">ATS Compatibility</h3>
                <ScoreRing
                  score={overallScore}
                  size={176}
                  strokeWidth={12}
                  label="/ 100"
                  showBadge
                />
                <p className="mt-5 text-sm text-gray-500 text-center max-w-[260px] leading-relaxed">
                  {overallScore >= 80 ? 'Great match! Your resume is well-optimized for this position.' :
                   overallScore >= 60 ? 'Good match, but there\'s room for improvement.' :
                   'Needs improvement. Consider tailoring your resume more closely to this job.'}
                </p>
              </div>

              <div className="flex-1 w-full md:pt-10 space-y-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Breakdown</h4>
                {scoreResult.keyword_analysis && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <FiKey className="h-3 w-3" />
                      <span>{matchedCount} of {keywordTotal} keywords matched</span>
                    </div>
                    <ScoreBar score={keywordPct} label="Keywords" size="lg" />
                  </div>
                )}
                {scoreResult.formatting_score !== undefined && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <FiLayout className="h-3 w-3" />
                      <span>ATS-friendly formatting</span>
                    </div>
                    <ScoreBar score={scoreResult.formatting_score} label="Formatting" size="lg" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Keyword Analysis */}
          {scoreResult.keyword_analysis && (
            <div className="card overflow-hidden">
              <div className="px-6 py-6 sm:p-8">
                <h3 className="text-base font-semibold text-gray-800 mb-5">Keyword Analysis</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="rounded-xl bg-emerald-50/40 border border-emerald-100/80 p-5 transition-shadow hover:shadow-sm">
                    <h4 className="text-sm font-medium text-emerald-700 mb-3 flex items-center">
                      <FiCheckCircle className="mr-2 h-4 w-4" />
                      Found Keywords
                      <span className="ml-auto text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-0.5 tabular-nums">{matchedCount}</span>
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {scoreResult.keyword_analysis.matched?.map((keyword, idx) => (
                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/90 text-emerald-800 ring-1 ring-emerald-200/60 shadow-sm">
                          {keyword}
                        </span>
                      ))}
                      {matchedCount === 0 && (
                        <p className="text-sm text-gray-500">No matching keywords found</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl bg-rose-50/40 border border-rose-100/80 p-5 transition-shadow hover:shadow-sm">
                    <h4 className="text-sm font-medium text-rose-700 mb-3 flex items-center">
                      <FiAlertCircle className="mr-2 h-4 w-4" />
                      Missing Keywords
                      <span className="ml-auto text-xs font-bold bg-rose-100 text-rose-700 rounded-full px-2.5 py-0.5 tabular-nums">{missingCount}</span>
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {scoreResult.keyword_analysis.missing?.map((keyword, idx) => (
                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/90 text-rose-800 ring-1 ring-rose-200/60 shadow-sm">
                          {keyword}
                        </span>
                      ))}
                      {missingCount === 0 && (
                        <p className="text-sm text-gray-500">No missing keywords</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {scoreResult.recommendations && scoreResult.recommendations.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-6 py-6 sm:p-8">
                <h3 className="text-base font-semibold text-gray-800 mb-5">Recommendations</h3>
                <ul className="space-y-3">
                  {scoreResult.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <FiCheckCircle className="h-4 w-4 text-primary-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600 leading-relaxed">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
};

export default ATSScoring;
